import React, { useEffect, useState } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import Spinner from "../components/ui/spinner";
import ErrorBoundary from "../components/ErrorBoundary";
import NotFoundPage from "../components/pages/NotFoundPage";
import { adminAuthApi } from "./api/auth";
import { ApiError } from "./api/utils";
import AdminHeader from "./components/AdminHeader";
import AdminLoginPage from "./pages/LoginPage";
import WorkspacesPage from "./pages/WorkspacesPage";
import WorkspacePage from "./pages/WorkspacePage";
import AdminEmployeePage from "./pages/AdminEmployeePage";

const AdminApp: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const [isSignedIn, setIsSignedIn] = useState<boolean | null>(null);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const checkAuth = async (isMountedRef: { current: boolean }) => {
    try {
      const signedIn = await adminAuthApi.isSignedIn();
      if (isMountedRef.current) setIsSignedIn(signedIn);
    } catch {
      if (isMountedRef.current) setIsSignedIn(false);
    }
  };

  useEffect(() => {
    const isMountedRef = { current: true };
    checkAuth(isMountedRef);
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleLoginSuccess = () => {
    setIsSignedIn(true);
    navigate("/admin/workspaces");
  };

  const handleSignOut = async () => {
    if (!window.confirm("Are you sure you want to sign out?")) return;

    setIsSigningOut(true);
    try {
      await adminAuthApi.signOut();
      setIsSignedIn(false);
      navigate("/admin/login");
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        console.error(err.message);
      }
    } finally {
      setIsSigningOut(false);
    }
  };

  if (isSignedIn === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Spinner size={32} className="text-muted-foreground" />
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <ErrorBoundary>
        <Routes>
          <Route
            path="/login"
            element={<AdminLoginPage onSuccess={handleLoginSuccess} />}
          />
          <Route path="*" element={<Navigate to="/admin/login" replace />} />
        </Routes>
      </ErrorBoundary>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader onSignOut={handleSignOut} isSigningOut={isSigningOut} />
      <ErrorBoundary>
        <main className="pb-8">
          <Routes>
            <Route path="/login" element={<Navigate to="/admin/workspaces" replace />} />
            <Route path="/" element={<Navigate to="/admin/workspaces" replace />} />
            <Route path="/workspaces" element={<WorkspacesPage />} />
            <Route path="/workspaces/:id" element={<WorkspacePage />} />
            <Route
              path="/workspaces/:workspaceId/employees/:employeeId"
              element={<AdminEmployeePage />}
            />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
      </ErrorBoundary>
    </div>
  );
};

export default AdminApp;
